import React, { FC } from "react";
import { Helmet } from "react-helmet";
import Input from "shared/Input/Input";
import { Link, useHistory } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useDispatch, useSelector } from "react-redux";
import { RootStore } from "functions/redux/store";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { checkLogin, emailSignUp, getFullCreds, LOGIN_FALSE } from "functions/redux/user/action";
import Loader from "react-loader-spinner";
import NcModal from "shared/NcModal/NcModal";
import { useTranslation } from "react-i18next";

export interface PageSignUpProps {
    className?: string;
}

export function modalContent(txt: string) {
    return <div className="grid grid-cols-1">
        <span className="w-48 block mt-5 text-neutral-500 dark:text-neutral-400 text-center">
            {txt}
        </span>
    </div>
}

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
    const { user } = useSelector((state: RootStore) => state);
    const dispatch = useDispatch();
    const history = useHistory();
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState("");
    const [modal, setModal] = React.useState(false);
    const [content, setContent] = React.useState(<div></div>);
    const { t } = useTranslation();

    React.useEffect(() => {
        if (user.credential == null) {
            dispatch(checkLogin());
        }
    }, [dispatch, user.credential]);

    React.useEffect(() => {
        if (user.credential != null && user.credential !== LOGIN_FALSE && user.credential.profile != null) {
            history.replace("/profile");
        }
    }, [user.credential, history]);

    async function register(e?: React.FormEvent) {
        if (e) e.preventDefault();
        if (!email || !password) {
            setContent(modalContent("Sila isi e-mel dan kata laluan"));
            setModal(true);
            return;
        }

        if (loading === "") {
            setLoading('email');
            try {
                const auth = getAuth();
                var result = await createUserWithEmailAndPassword(auth, email, password);
                var idToken = await result.user.getIdTokenResult();
                await dispatch(emailSignUp({
                    token: idToken.token,
                    fcm: null,
                    firstName: email.split('@')[0] || email,
                    lastName: ""
                }));
                await dispatch(getFullCreds());
                setLoading("");
                history.replace("/profile");
            } catch (err: any) {
                console.error("SignUp Error:", err);
                let msg = t('an_error_occured');
                if (err?.code === 'auth/email-already-in-use') {
                    msg = "E-mel ini telah terdaftar. Sila log masuk.";
                } else if (err?.code === 'auth/weak-password') {
                    msg = "Kata laluan terlalu lemah. Sila guna sekurang-kurangnya 6 aksara.";
                }
                setContent(modalContent(msg));
                setModal(true);
                setLoading("");
            }
        }
    }

    return (
        <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
            <NcModal
                isOpenProp={modal}
                renderTrigger={() => <div></div>}
                renderContent={() => content}
                onCloseModal={() => setModal(false)}
                modalTitle="Information"
            />
            <Helmet>
                <title>{t('sign_up')}</title>
            </Helmet>
            <div className="container mb-24 lg:mb-32">
                <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
                    GuideSantai
                </h2>
                <div className="max-w-md mx-auto space-y-6">
                    <span className="block text-center text-base md:text-lg text-neutral-500 dark:text-neutral-400">
                        {t('find_guide_santai')}
                    </span>
                    <form className="grid grid-cols-1 gap-6" onSubmit={register}>
                        <label className="block">
                            <span className="text-neutral-800 dark:text-neutral-200 font-medium text-sm">
                                {t('mail_addr')}
                            </span>
                            <Input
                                type="email"
                                placeholder="example@example.com"
                                className="mt-1"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </label>
                        <label className="block">
                            <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200 font-medium text-sm">
                                {t('password')}
                            </span>
                            <Input
                                type="password"
                                className="mt-1"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </label>
                        <ButtonPrimary type="submit" className="w-full">
                            {loading === 'email' ? (
                                <Loader
                                    type="Circles"
                                    color="#ffffff"
                                    height={25}
                                    width={25}
                                />
                            ) : (
                                t('continue')
                            )}
                        </ButtonPrimary>
                    </form>

                    <span className="block text-center text-sm text-neutral-700 dark:text-neutral-300">
                        {t('already_sign_up')}{' '}
                        <Link to="/login" className="font-semibold text-primary-6000 hover:underline">
                            {t('login')}
                        </Link>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default PageSignUp;

